import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, BookOpen, Scroll, Lightbulb, ArrowRight, Play } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAudio } from "../context/AudioContext";
import { LANGUAGES } from "../lib/constants";

export default function DailyPlan() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { play, language } = useAudio();

  useEffect(() => {
    api.get("/recommendations/today", { params: { lang: language || "en" } })
      .then((r) => setPlan(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [language]);

  const readAll = () => {
    if (!plan) return;
    const items = [];
    if (plan.reflection) items.push({ id: "reflection", text: plan.reflection.replace(/[*_#]/g, "") });
    if (plan.chapter) items.push({ id: "chapter", text: `Today's chapter: ${plan.chapter.book_name} ${plan.chapter.chapter}. ${plan.chapter.blurb}.` });
    if (plan.precept) items.push({ id: "precept", text: `Today's precept number ${plan.precept.number}: ${plan.precept.command}. From ${plan.precept.reference}.` });
    if (plan.word) items.push({ id: "word", text: `Today's word: ${plan.word.term}. ${plan.word.definition}` });
    const cfg = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];
    play(items, { title: "Today's reading plan" }, language || "en", cfg.voice, { translate: language && language !== "en" });
  };

  if (loading) {
    return (
      <section className="bg-cream-100 border border-cream-300 rounded-3xl p-6 md:p-8" data-testid="daily-plan-loading">
        <div className="h-4 w-32 bg-cream-300 rounded animate-pulse" />
        <div className="mt-3 h-6 w-2/3 bg-cream-300 rounded animate-pulse" />
      </section>
    );
  }
  if (error || !plan) return null;

  return (
    <section
      data-testid="daily-plan"
      className="bg-gradient-to-br from-forest to-forest-600 text-cream rounded-3xl p-6 md:p-8 relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 80% 0%, rgba(231,196,124,0.5) 0%, transparent 50%)" }} />
      <div className="relative">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkle size={14} weight="fill" className="text-[#E7C47C]" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-cream/80 font-semibold">Today's Reading Plan</span>
          </div>
          <button
            onClick={readAll}
            data-testid="daily-plan-read"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-cream/15 hover:bg-cream/25 text-xs font-semibold tracking-wider uppercase transition-all"
          >
            <Play size={12} weight="fill" /> Read aloud
          </button>
        </div>

        {plan.reflection && (
          <p
            className="font-heading text-xl md:text-2xl text-cream leading-relaxed mt-3 italic"
            data-testid="daily-plan-reflection"
            dangerouslySetInnerHTML={{
              __html: plan.reflection
                .replace(/^\*\*(.+?)\*\*$/gm, '<strong class="not-italic block mb-2 text-[#E7C47C]">$1</strong>')
                .replace(/\*\*(.+?)\*\*/g, '<strong class="not-italic text-[#E7C47C]">$1</strong>')
                .replace(/\*(.+?)\*/g, '<span class="text-cream/85">$1</span>')
                .split("\n").map((line) => line.trim()).filter(Boolean).join("<br/><br/>"),
            }}
          />
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          {plan.chapter && (
            <Link
              to={`/bible/${plan.chapter.book}/${plan.chapter.chapter}`}
              data-testid="daily-plan-chapter"
              className="group bg-cream/10 hover:bg-cream/20 border border-cream/15 rounded-2xl p-4 transition-all"
            >
              <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#E7C47C] font-semibold">
                <BookOpen size={11} weight="fill" /> Chapter
              </div>
              <p className="font-heading text-lg mt-1.5 leading-tight">{plan.chapter.book_name} {plan.chapter.chapter}</p>
              <p className="text-xs text-cream/70 mt-1 line-clamp-2">{plan.chapter.blurb}</p>
              <ArrowRight size={12} className="mt-2 opacity-60 group-hover:translate-x-1 transition-all" />
            </Link>
          )}
          {plan.precept && (
            <Link
              to="/precepts"
              data-testid="daily-plan-precept"
              className="group bg-cream/10 hover:bg-cream/20 border border-cream/15 rounded-2xl p-4 transition-all"
            >
              <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#E7C47C] font-semibold">
                <Scroll size={11} weight="fill" /> Precept #{plan.precept.number}
              </div>
              <p className="text-sm mt-1.5 leading-snug line-clamp-2">{plan.precept.command}</p>
              <p className="text-[10px] text-cream/60 mt-1">{plan.precept.reference}</p>
            </Link>
          )}
          {plan.word && (
            <Link
              to="/dictionary"
              data-testid="daily-plan-word"
              className="group bg-cream/10 hover:bg-cream/20 border border-cream/15 rounded-2xl p-4 transition-all"
            >
              <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#E7C47C] font-semibold">
                <Lightbulb size={11} weight="fill" /> Word of the day
              </div>
              <p className="font-heading text-lg mt-1.5 leading-tight">{plan.word.term}</p>
              <p className="text-xs text-cream/70 mt-1 line-clamp-2">{plan.word.definition}</p>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
